import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  fetchCompanyPayoutDetails,
  fetchMyCompany,
  upsertCompanyPayoutDetails,
} from '../services/api/companyOwnerAuth';
import {
  getCompanyOwnerAuthUser,
  saveCompanyOwnerCompany,
  saveCompanyOwnerPayoutDetails,
} from '../services/storage/companyOwnerFlow';
import type { CompanyAccountType, CompanyPayoutDetails } from '../types/companyOwner';
import {
  formatDateTime,
  getNonCompanyAccessFallbackRoute,
  isCompanyOwnerUser,
} from '../utils/companyOwner';
import '../styles/register.css';
import '../styles/company-owner.css';

const ACCOUNT_TYPE_OPTIONS: Array<{ label: string; value: CompanyAccountType }> = [
  { label: 'Cheque', value: 'CHEQUE' },
  { label: 'Savings', value: 'SAVINGS' },
  { label: 'Transmission', value: 'TRANSMISSION' },
];

const CompanyOwnerPayoutSetupPage: React.FC = () => {
  const navigate = useNavigate();
  const companyOwnerUser = useMemo(() => getCompanyOwnerAuthUser(), []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [payoutDetails, setPayoutDetails] = useState<CompanyPayoutDetails | null>(null);
  const [bankLetterFile, setBankLetterFile] = useState<File | null>(null);
  const [formData, setFormData] = useState({
    accountHolderName: '',
    bankName: '',
    accountNumber: '',
    branchCode: '',
    accountType: 'CHEQUE' as CompanyAccountType,
  });

  useEffect(() => {
    if (!isCompanyOwnerUser(companyOwnerUser)) {
      navigate(getNonCompanyAccessFallbackRoute(), { replace: true });
      return;
    }

    let mounted = true;

    const bootstrap = async () => {
      setLoading(true);
      setError('');

      try {
        const company = await fetchMyCompany();
        if (!mounted) return;

        if (!company) {
          navigate('/company/onboarding', { replace: true });
          return;
        }

        saveCompanyOwnerCompany(company);

        const nextPayoutDetails = await fetchCompanyPayoutDetails();
        if (!mounted) return;

        saveCompanyOwnerPayoutDetails(nextPayoutDetails);
        setPayoutDetails(nextPayoutDetails);
        setFormData((current) => ({
          ...current,
          accountHolderName: nextPayoutDetails.accountHolderName ?? current.accountHolderName,
          bankName: nextPayoutDetails.bankName ?? current.bankName,
          branchCode: nextPayoutDetails.branchCode ?? current.branchCode,
          accountType: (nextPayoutDetails.accountType as CompanyAccountType | null) ?? current.accountType,
        }));
      } catch (bootstrapError: any) {
        if (!mounted) return;
        setError(bootstrapError.message || 'Could not load company payout setup.');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void bootstrap();

    return () => {
      mounted = false;
    };
  }, [companyOwnerUser, navigate]);

  const setField =
    (field: keyof typeof formData) =>
    (
      event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    ) => {
      const rawValue = event.target.value;
      const nextValue =
        field === 'branchCode' || field === 'accountNumber'
          ? rawValue.replace(/\D/g, '')
          : rawValue;

      setFormData((current) => ({ ...current, [field]: nextValue }));
      setFieldErrors((current) => {
        if (!current[field]) return current;
        const nextErrors = { ...current };
        delete nextErrors[field];
        return nextErrors;
      });
    };

  const handleBankLetterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] ?? null;
    setBankLetterFile(selectedFile);
    setFieldErrors((current) => {
      if (!current.bankLetter) return current;
      const nextErrors = { ...current };
      delete nextErrors.bankLetter;
      return nextErrors;
    });
  };

  const validateForm = (): boolean => {
    const nextErrors: Record<string, string> = {};

    if (!formData.accountHolderName.trim()) {
      nextErrors.accountHolderName = 'Enter the company account holder name.';
    }

    if (!formData.bankName.trim()) {
      nextErrors.bankName = 'Enter the bank name.';
    }

    if (!/^\d{6,20}$/.test(formData.accountNumber.trim())) {
      nextErrors.accountNumber = 'Account number must contain 6 to 20 digits.';
    }

    const bankLetterRequired = !payoutDetails?.bankLetterPresent;
    if (bankLetterRequired && !bankLetterFile) {
      nextErrors.bankLetter = 'Upload the company bank letter PDF.';
    } else if (bankLetterFile) {
      const lowerName = bankLetterFile.name.toLowerCase();
      const isPdf =
        bankLetterFile.type === 'application/pdf' || lowerName.endsWith('.pdf');
      if (!isPdf) {
        nextErrors.bankLetter = 'Bank letter must be uploaded as a PDF.';
      }
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateForm()) {
      setError('Please fix the payout details highlighted below.');
      setSuccessMessage('');
      return;
    }

    setSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      const saved = await upsertCompanyPayoutDetails({
        accountHolderName: formData.accountHolderName.trim(),
        bankName: formData.bankName.trim(),
        accountNumber: formData.accountNumber.trim(),
        branchCode: formData.branchCode.trim() || undefined,
        accountType: formData.accountType.toUpperCase(),
        bankLetter: bankLetterFile,
      });

      saveCompanyOwnerPayoutDetails(saved);
      setPayoutDetails(saved);
      setFormData((current) => ({ ...current, accountNumber: '' }));
      setBankLetterFile(null);
      setSuccessMessage(
        saved.payoutStatus === 'ACTIVE'
          ? 'Company payout details are active.'
          : 'Company payout details submitted. Settlements stay on hold until setup is approved.',
      );
      navigate('/company/dashboard', { replace: true });
    } catch (submitError: any) {
      setError(submitError.message || 'Could not save company payout details.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="register-page">
        <div className="register-shell register-shell-single">
          <div className="register-panel register-panel-form register-status-panel">
            <div className="register-loader" />
            <h1 className="register-form-title">Loading payout setup</h1>
            <p className="register-form-copy">
              We are checking whether your company still needs settlement bank details.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="register-page">
      <div className="register-shell register-shell-single">
        <aside className="register-panel register-panel-brand">
          <div className="register-brand-lockup">
            <div className="register-brand-badge">Company Payout Setup</div>
            <h1 className="register-brand-title">Add the bank account for company-driver settlements.</h1>
            <p className="register-brand-copy">
              ShiftWagon uses these company payout details when settling earnings for drivers linked
              to your company. This is separate from cashout tools, which are still coming in a
              later release.
            </p>
          </div>

          <div className="register-benefit-list">
            <div className="register-benefit-item">
              <span className="register-benefit-dot" />
              <span>Use the company account that should receive provider-managed settlements once setup is verified.</span>
            </div>
            <div className="register-benefit-item">
              <span className="register-benefit-dot" />
              <span>Company-driver payouts remain on hold until this setup is submitted.</span>
            </div>
            <div className="register-benefit-item">
              <span className="register-benefit-dot" />
              <span>You can update the account later, but backend may mark it for review again.</span>
            </div>
          </div>
        </aside>

        <div className="register-panel register-panel-form">
          <div className="register-form-header company-payout-header">
            <div className="register-form-badge">Settlement Details</div>
            <h1 className="register-form-title company-payout-title">
              Complete your company payout details
            </h1>
            <p className="register-form-copy">
              Submit the bank account your company wants ShiftWagon to use for company-driver
              settlements.
            </p>
          </div>

          {payoutDetails ? (
            <div className="company-payout-summary-card">
              <div className="company-payout-summary-row">
                <span className="company-payout-summary-icon">B</span>
                <div>
                  <div className="company-payout-summary-title">Current payout status</div>
                  <div className="company-payout-summary-meta">
                    <span
                      className={`company-status-pill company-status-pill-${String(
                        payoutDetails.payoutStatus || 'missing',
                      ).toLowerCase()}`}
                    >
                      {payoutDetails.payoutStatus}
                    </span>
                  </div>
                </div>
              </div>
              <p className="company-payout-summary-copy">
                {payoutDetails.payoutDetailsPresent
                  ? `Saved account: ${payoutDetails.bankName || 'Bank not available'} ${payoutDetails.maskedAccountNumber || ''}`.trim()
                  : 'No payout details have been submitted for this company yet.'}
              </p>
              {payoutDetails.updatedAt ? (
                <p className="company-payout-summary-copy">
                  Last updated: {formatDateTime(payoutDetails.updatedAt)}
                </p>
              ) : null}
              {payoutDetails.bankLetterPresent ? (
                <p className="company-payout-summary-copy">
                  Bank letter: {payoutDetails.bankLetterFileName || 'PDF uploaded'}
                  {payoutDetails.bankLetterUploadedAt
                    ? ` • Uploaded ${formatDateTime(payoutDetails.bankLetterUploadedAt)}`
                    : ''}
                </p>
              ) : (
                <p className="company-payout-summary-copy">
                  No bank letter PDF has been uploaded yet.
                </p>
              )}
            </div>
          ) : null}

          {error ? <div className="register-inline-error">{error}</div> : null}
          {successMessage ? <div className="register-inline-success">{successMessage}</div> : null}

          <form className="register-form-grid" onSubmit={handleSubmit}>
            <label className="register-field">
              <span>Account Holder Name</span>
              <input
                className="register-input"
                onChange={setField('accountHolderName')}
                placeholder="ShiftWagon Logistics Pty Ltd"
                value={formData.accountHolderName}
              />
              {fieldErrors.accountHolderName ? (
                <small className="register-field-error">{fieldErrors.accountHolderName}</small>
              ) : null}
            </label>

            <label className="register-field">
              <span>Bank Name</span>
              <input
                className="register-input"
                onChange={setField('bankName')}
                placeholder="FNB"
                value={formData.bankName}
              />
              {fieldErrors.bankName ? (
                <small className="register-field-error">{fieldErrors.bankName}</small>
              ) : null}
            </label>

            <label className="register-field">
              <span>Branch Code (Optional)</span>
              <input
                className="register-input"
                inputMode="numeric"
                onChange={setField('branchCode')}
                placeholder="250655"
                value={formData.branchCode}
              />
              <small className="register-field-hint">
                Leave this blank if your bank account does not use a branch code for settlements.
              </small>
              {fieldErrors.branchCode ? (
                <small className="register-field-error">{fieldErrors.branchCode}</small>
              ) : null}
            </label>

            <label className="register-field">
              <span>Account Type</span>
              <select
                className="register-input"
                onChange={setField('accountType')}
                value={formData.accountType}
              >
                {ACCOUNT_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="register-field register-field-full">
              <span>Account Number</span>
              <input
                className="register-input"
                inputMode="numeric"
                onChange={setField('accountNumber')}
                placeholder={
                  payoutDetails?.maskedAccountNumber
                    ? `Enter the full account number again to update ${payoutDetails.maskedAccountNumber}`
                    : '1234567890'
                }
                value={formData.accountNumber}
              />
              {fieldErrors.accountNumber ? (
                <small className="register-field-error">{fieldErrors.accountNumber}</small>
              ) : null}
            </label>

            <label className="register-field register-field-full">
              <span>Bank Letter PDF</span>
              <input
                accept="application/pdf,.pdf"
                className="register-input"
                onChange={handleBankLetterChange}
                type="file"
              />
              <small className="register-field-hint">
                Upload the latest stamped or issued bank letter as a PDF.
                {payoutDetails?.bankLetterPresent
                  ? ` Current file: ${payoutDetails.bankLetterFileName || 'PDF uploaded'}. Leave blank if you do not want to replace it.`
                  : ''}
              </small>
              {bankLetterFile ? (
                <small className="register-field-hint">Selected file: {bankLetterFile.name}</small>
              ) : null}
              {fieldErrors.bankLetter ? (
                <small className="register-field-error">{fieldErrors.bankLetter}</small>
              ) : null}
            </label>

            <div className="register-highlight-card register-field-full">
              <p className="register-highlight-copy">
                By saving these details, you confirm this account belongs to the company or is
                authorized to receive company settlements from ShiftWagon.
              </p>
            </div>

            <div className="register-flow-actions register-field-full">
              <Link className="register-secondary-button" to="/company/dashboard">
                Back to dashboard
              </Link>
              <button className="register-submit register-submit-inline" disabled={saving} type="submit">
                {saving ? 'Saving payout details...' : 'Save payout details'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default CompanyOwnerPayoutSetupPage;
